import { type JSX, type ReactElement, type ReactNode } from 'react'
import cx from 'classnames'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Tooltip } from '@/components/Tooltip'
import { UnreadBadge } from '@/components/UnreadBadge'
import { links } from '@/utils/link'
import { NavLink } from 'react-router'
import styles from './Sidebar.module.css'
import { useIsRailCollapsed } from '@/hooks/viewport-hooks'

export interface SidebarProps {
  floating: boolean
  /**
   * Whether to leave out the rail, so the content gets the whole window.
   */
  isRailHidden?: boolean
  children?: ReactNode
  logoIconWhite: string
  workspaceName: string
  sections: NavSection[]
  accountAvatar: AvatarSubject
  accountLabel: string
  onAccountPress: () => unknown
  profileMenu?: ReactNode
  onToggleSectionCollapsed: (key: string) => unknown
}

export function Sidebar({
  children,
  floating,
  isRailHidden,
  logoIconWhite,
  workspaceName,
  sections,
  accountAvatar,
  accountLabel,
  onAccountPress,
  profileMenu,
  onToggleSectionCollapsed,
}: SidebarProps) {
  return (
    <div
      className={cx(
        styles.root,
        floating && styles.floating,
        isRailHidden && styles.railHidden,
      )}
      data-test="RootNavigation"
    >
      {!isRailHidden && (
        <nav className={styles.rail}>
          <a
            href={links.welcome()}
            className={styles.brand}
            title={`Worklayer Portal v${process.env.__VERSION__}`}
            data-test="Logo"
          >
            <img className={styles.brandMark} src={logoIconWhite} alt="" />
            <span className={styles.brandName}>{workspaceName}</span>
          </a>
          <div className={styles.pages}>
            {sections.map((section) => (
              <SidebarSection
                section={section}
                onToggleCollapsed={onToggleSectionCollapsed}
                key={section.key}
              />
            ))}
          </div>
          <div className={styles.footer}>
            <div className={styles.account}>
              <RawButton
                className={cx(styles.item, styles.accountButton)}
                onPress={(e) => {
                  e.preventDefault()
                  return onAccountPress()
                }}
                testId="ProfileButton"
              >
                <span className={styles.itemIcon}>
                  <Avatar size={22} subject={accountAvatar} />
                </span>
                <span className={styles.itemLabel}>{accountLabel}</span>
              </RawButton>
              {profileMenu}
            </div>
          </div>
        </nav>
      )}
      <div className={styles.children}>{children}</div>
    </div>
  )
}

export type NavItem = {
  name: string
  title: string
  icon: JSX.Element
  hidden?: boolean
  badgeCount?: number
  badgePill?: boolean
} & (
  | { kind: 'page'; path: string; exact?: boolean }
  | { kind: 'external'; href: string }
  | { kind: 'action'; onPress: () => unknown; active?: boolean }
)

export interface NavSection {
  key: string
  title?: string
  hidden?: boolean
  collapsed?: boolean
  items: NavItem[]
}

interface SidebarSectionProps {
  section: NavSection
  onToggleCollapsed: (key: string) => unknown
}

function SidebarSection({ section, onToggleCollapsed }: SidebarSectionProps) {
  const collapsed = !!section.collapsed
  const items = section.items.filter((item) => !item.hidden)
  if (section.hidden || items.length === 0) {
    return null
  }

  return (
    <div className={styles.section}>
      {section.title && (
        <RawButton
          className={styles.sectionHeader}
          onPress={() => onToggleCollapsed(section.key)}
          aria-expanded={!collapsed}
          testId={`nav-section-${section.key}`}
        >
          {section.title}
          <Icon
            name="chevron-down"
            size={10}
            aria-hidden="true"
            className={cx(styles.sectionCaret, collapsed && styles.collapsed)}
          />
        </RawButton>
      )}
      {!collapsed &&
        items.map((item) => <SidebarItem item={item} key={item.name} />)}
    </div>
  )
}

interface SidebarItemProps {
  item: NavItem
}

function SidebarItem({ item }: SidebarItemProps) {
  const content = (
    <>
      <span className={styles.itemIcon}>{item.icon}</span>
      <span className={styles.itemLabel}>{item.title}</span>
      {!!item.badgeCount && (
        <>
          <span className={item.badgePill ? styles.countPill : styles.count}>
            {item.badgeCount}
          </span>
          <span className={styles.badge}>
            <UnreadBadge count={item.badgeCount} />
          </span>
        </>
      )}
    </>
  )

  return (
    <RailTooltip content={item.title}>
      {item.kind === 'page' ? (
        <NavLink
          className={({ isActive }) =>
            cx(styles.item, isActive && styles.active)
          }
          to={item.path}
          end={item.exact}
          data-test={item.name}
        >
          {content}
        </NavLink>
      ) : item.kind === 'external' ? (
        <a
          className={styles.item}
          href={item.href}
          target="_blank"
          rel="noreferrer"
          data-test={item.name}
        >
          {content}
        </a>
      ) : (
        <RawButton
          className={cx(styles.item, item.active && styles.active)}
          onPress={(e) => {
            e.preventDefault()
            item.onPress()
          }}
          testId={item.name}
        >
          {content}
        </RawButton>
      )}
    </RailTooltip>
  )
}

interface RailTooltipProps {
  content: ReactNode
  children: ReactElement
}

/**
 * Labels are visible on the full rail, so a tooltip there would only repeat
 * them; it earns its place once the rail collapses to icons.
 */
function RailTooltip({ content, children }: RailTooltipProps) {
  const collapsed = useIsRailCollapsed()
  if (!collapsed) {
    return children
  }
  return (
    <Tooltip content={content} placement="right">
      {children}
    </Tooltip>
  )
}
